import { useAuth } from "AuthProvider";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  FormikTextField,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import DateField from "../EventPage/DateField";
import ImageDrop from "../EventPage/ImageDrop";
import styles from "./BlogPage.module.scss";

interface BlogForm {
  tittel: string;
  tekst: string;
  plakat?: File | null;
  activated: boolean;
}

interface Blog {
  id: number;
  tittel: string;
  tekst: string;
  date: string;
  plakat?: string;
  activated: boolean;
}

const validationSchema = Yup.object().shape({
  tittel: Yup.string().required("Overskrift er påkrevd"),
  tekst: Yup.string().required("Innhold er påkrevd"),
  plakat: Yup.mixed(),
});

const BlogPage: React.FC = () => {
  const { selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: blogsData,
    isLoading: isBlogsLoading,
    execute: fetchBlogs,
  } = useFetch<Blog[]>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageToShow, setImageToShow] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageClick = (image: string) => {
    setImageToShow(image);
    setShowImageModal(true);
  };

  const fetchBlogList = async () => {
    const response = await fetchBlogs(
      `/api/v1/blogPost/org/${selectedOrgNumber}`,
      "GET",
    );
    if (response) {
      setBlogs(response);
    }
  };

  useEffect(() => {
    fetchBlogList();
  }, [selectedOrgNumber]);

  const handleFileChange = (file: File | null) => {
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (values: BlogForm, { resetForm }: any) => {
    if (!selectedDate) {
      setDateError("Dato er påkrevd");
      return;
    } else {
      setDateError(null);
    }

    const currentTime = new Date();
    const hours = String(currentTime.getHours()).padStart(2, "0");
    const minutes = String(currentTime.getMinutes()).padStart(2, "0");

    const formattedDate = `${selectedDate.getFullYear()}-${String(
      selectedDate.getMonth() + 1,
    ).padStart(
      2,
      "0",
    )}-${String(selectedDate.getDate()).padStart(2, "0")} ${hours}:${minutes}`;

    const blogRequestDto = {
      date: formattedDate,
      tittel: values.tittel,
      tekst: values.tekst,
      mosqueOrgNr: selectedOrgNumber,
      activated: values.activated,
      plakat: "",
    };

    const formData = new FormData();
    if (values.plakat) {
      formData.append("file", values.plakat);
    }
    formData.append("blogPostRequestDto", JSON.stringify(blogRequestDto));

    try {
      const response = await execute(
        "/api/v1/blogPost/addWithImage",
        "POST",
        formData,
      );
      resetForm();
      setSelectedDate(null);
      setImagePreview(null);
      setBlogs((prevBlogs) => [...prevBlogs, response]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      fetchBlogList();
      if (response) {
        toast.success("Innlegget er publisert.");
      } else {
        toast.error("Kunne ikke lagre innlegget. Prøv igjen.");
      }
    } catch (error: any) {
      console.error("Error:", error.response || error.message || error);
    }
  };

  const deleteBlog = async (id: number) => {
    try {
      await execute(`/api/v1/blogPost/${id}`, "DELETE");
      setBlogs((prevBlogs) => prevBlogs.filter((blog) => blog.id !== id));
      toast.success("Innlegget er slettet.");
    } catch (error: any) {
      console.error("Failed to delete blog", error);
    }
  };

  const confirmDeleteBlog = async (blog: Blog) => {
    const ok = await confirm({
      title: "Slett innlegg?",
      message: (
        <span>
          Innlegget <b>«{blog.tittel}»</b> fjernes fra appen. Dette kan ikke
          angres.
        </span>
      ),
      confirmLabel: "Slett innlegg",
      tone: "danger",
    });
    if (ok) await deleteBlog(blog.id);
  };

  useEffect(() => {
    if (!showModal && !showImageModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setShowImageModal(false);
      setShowModal(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal, showImageModal]);

  const formatDate = (date: string) => {
    const parsed = new Date(date);
    return isNaN(parsed.getTime())
      ? date
      : parsed.toLocaleDateString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  const openEditModal = (blog: Blog) => {
    setEditingBlog(blog);
    setShowModal(true);
    setEditImagePreview(blog.plakat || null);
  };

  const handleEditFileChange = (file: File | null) => {
    if (file) {
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveEdit = async (values: BlogForm) => {
    if (!editingBlog) return;

    const currentTime = new Date();
    const hours = String(currentTime.getHours()).padStart(2, "0");
    const minutes = String(currentTime.getMinutes()).padStart(2, "0");

    const formattedDate = `${currentTime.getFullYear()}-${String(
      currentTime.getMonth() + 1,
    ).padStart(
      2,
      "0",
    )}-${String(currentTime.getDate()).padStart(2, "0")} ${hours}:${minutes}`;

    const blogRequestDto = {
      date: formattedDate,
      tittel: values.tittel,
      tekst: values.tekst,
      plakat: editImagePreview || editingBlog.plakat || "",
      mosqueOrgNr: selectedOrgNumber,
      activated: values.activated,
    };

    const formData = new FormData();
    formData.append("blogPostRequestDto", JSON.stringify(blogRequestDto));
    if (values.plakat) {
      formData.append("file", values.plakat);
    }

    try {
      await execute(`/api/v1/blogPost/${editingBlog.id}`, "PATCH", formData);
      setBlogs((prevBlogs) =>
        prevBlogs.map((blog) =>
          blog.id === editingBlog.id
            ? {
                ...blog,
                tittel: values.tittel,
                tekst: values.tekst,
                activated: values.activated,
                plakat: editImagePreview || blog.plakat || "",
                date: formattedDate,
              }
            : blog,
        ),
      );

      setShowModal(false);
      setEditingBlog(null);
      setEditImagePreview(null);
      toast.success("Endringene er lagret.");
    } catch (error) {
      console.error("Failed to update blog", error);
    }
  };

  return (
    <>
      <PageHeader
        title="Blogginnlegg"
        subtitle="Artikler og nyheter fra moskeen."
      />

      <div className={styles.layout}>
        <Card title="Nytt blogginnlegg">
          <Formik
            initialValues={{
              tittel: "",
              tekst: "",
              plakat: null,
              activated: true,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values }) => (
              <FormikForm className={styles.form} noValidate>
                <FormikTextField
                  name="tittel"
                  label="Overskrift"
                  required
                  placeholder="Skriv overskrift …"
                />
                <FormikTextarea
                  name="tekst"
                  label="Innhold"
                  required
                  rows={3}
                  placeholder="Skriv innhold …"
                />
                <div className={ui.fld}>
                  <DateField
                    label="Dato"
                    required
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                  />
                  {dateError && (
                    <span className={ui.errt} role="alert">
                      {dateError}
                    </span>
                  )}
                </div>
                <ImageDrop
                  label="Overskriftsbilde"
                  required
                  preview={imagePreview}
                  inputRef={fileInputRef}
                  onFile={(file) => {
                    setFieldValue("plakat", file);
                    handleFileChange(file);
                  }}
                />
                <Toggle
                  label={
                    <span className={styles.toggleLabel}>Vis i appen</span>
                  }
                  name="activated"
                  checked={values.activated}
                  onChange={(e) => setFieldValue("activated", e.target.checked)}
                />
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={
                    !(isValid && dirty && selectedDate && values.plakat)
                  }
                  icon={<Icon name="check" size={16} strokeWidth={2} />}
                >
                  {isLoading ? "Laster opp …" : "Publiser innlegg"}
                </Button>
                {error && (
                  <Alert tone="error">Noe gikk galt. Prøv igjen senere.</Alert>
                )}
              </FormikForm>
            )}
          </Formik>
        </Card>

        <Card title="Alle innlegg">
          {isBlogsLoading ? (
            <div className={styles.skeletons}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={80} radius={12} />
              ))}
            </div>
          ) : error ? (
            <Alert tone="error">Kunne ikke hente innleggene.</Alert>
          ) : blogs.length > 0 ? (
            <ul className={styles.list}>
              {blogs.map((blog) => (
                <li key={blog.id} className={styles.item}>
                  {blog.plakat ? (
                    <button
                      type="button"
                      className={styles.thumb}
                      onClick={() =>
                        blog.plakat && handleImageClick(blog.plakat)
                      }
                      aria-label={`Vis bildet for ${blog.tittel}`}
                    >
                      <img src={blog.plakat} alt="" />
                    </button>
                  ) : (
                    <span className={styles.thumb} aria-hidden="true">
                      <Icon name="doc" size={20} />
                    </span>
                  )}
                  <span className={styles.itemBody}>
                    <span className={styles.itemTitleRow}>
                      <b className={styles.itemTitle}>{blog.tittel}</b>
                      {!blog.activated && (
                        <Badge tone="grey">Skjult i app</Badge>
                      )}
                    </span>
                    <span className={styles.itemText}>
                      {blog.tekst.length > 100
                        ? `${blog.tekst.substring(0, 100)}...`
                        : blog.tekst}
                    </span>
                    <span className={styles.itemDate}>
                      <Icon name="cal" size={14} strokeWidth={2} />
                      {formatDate(blog.date)}
                    </span>
                  </span>
                  <span
                    className={styles.visibility}
                    title={blog.activated ? "Vises i appen" : "Skjult i appen"}
                    aria-label={
                      blog.activated ? "Vises i appen" : "Skjult i appen"
                    }
                    role="img"
                  >
                    <svg
                      className={ui.icon}
                      viewBox="0 0 24 24"
                      width={17}
                      height={17}
                      strokeWidth={1.9}
                      aria-hidden="true"
                    >
                      <path
                        d={
                          blog.activated
                            ? "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"
                            : "M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2"
                        }
                      />
                    </svg>
                  </span>
                  <IconButton
                    label="Rediger"
                    icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                    onClick={() => openEditModal(blog)}
                  />
                  <IconButton
                    label="Slett"
                    icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
                    onClick={() => confirmDeleteBlog(blog)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="doc" title="Ingen innlegg">
              Innlegg du publiserer vises her.
            </EmptyState>
          )}
        </Card>
      </div>

      {showImageModal && imageToShow && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowImageModal(false);
          }}
        >
          <div
            className={`${ui.modal} ${styles.imageModal}`}
            role="dialog"
            aria-modal="true"
            aria-label="Bilde"
          >
            <div className={ui.mh}>
              Bilde
              <IconButton
                className={styles.modalClose}
                label="Lukk"
                icon={<Icon name="x" size={18} strokeWidth={2} />}
                onClick={() => setShowImageModal(false)}
              />
            </div>
            <div className={ui.mb}>
              <img
                src={imageToShow}
                alt="Bilde i full størrelse"
                className={styles.fullImage}
              />
            </div>
          </div>
        </div>
      )}

      {showModal && editingBlog && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div
            className={ui.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-blog-title"
          >
            <Formik
              initialValues={{
                tittel: editingBlog.tittel,
                tekst: editingBlog.tekst,
                activated: editingBlog.activated,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveEdit}
            >
              {({ isValid, values, setFieldValue, isSubmitting }) => (
                <FormikForm noValidate>
                  <h2 className={ui.mh} id="edit-blog-title">
                    <span className={ui.mhIcon} aria-hidden="true">
                      <Icon name="edit" size={18} />
                    </span>
                    Rediger innlegg
                    <IconButton
                      className={styles.modalClose}
                      label="Lukk"
                      icon={<Icon name="x" size={18} strokeWidth={2} />}
                      onClick={() => setShowModal(false)}
                    />
                  </h2>
                  <div className={ui.mb}>
                    <FormikTextField
                      name="tittel"
                      label="Overskrift"
                      required
                      placeholder="Skriv overskrift …"
                    />
                    <FormikTextarea
                      name="tekst"
                      label="Innhold"
                      required
                      rows={5}
                      placeholder="Skriv innhold …"
                    />
                    <Toggle
                      label={
                        <span className={styles.toggleLabel}>Vis i appen</span>
                      }
                      name="activated"
                      checked={values.activated}
                      onChange={(e) =>
                        setFieldValue("activated", e.target.checked)
                      }
                    />
                    <ImageDrop
                      label="Bytt bilde"
                      preview={editImagePreview}
                      onFile={(file) => {
                        setFieldValue("plakat", file);
                        handleEditFileChange(file);
                      }}
                    />
                  </div>
                  <div className={ui.mf}>
                    <Button
                      variant="secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Avbryt
                    </Button>
                    <Button
                      type="submit"
                      disabled={!isValid}
                      loading={isSubmitting}
                    >
                      Lagre endringer
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogPage;
